import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dumbbell, X, Clock, CheckCircle2, Loader2, ArrowLeft, Music, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import ExerciseCard from "../components/ExerciseCard";
import MobileCarousel from "../components/MobileCarousel";
import ThemeToggle from "../components/ThemeToggle";
import { haptic } from "../lib/haptics";
import { useSwipe, useIsMobile } from "../lib/useSwipe";

export default function SessionPage() {
  const { dayIndex } = useParams();
  const idx = Number(dayIndex);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    api
      .get("/workouts/current")
      .then((r) => setPlan(r.data?.plan || null))
      .catch((err) => console.error("session load failed", err))
      .finally(() => setLoading(false));
  }, []);

  const day = plan?.days?.[idx];
  const isMobile = useIsMobile();
  const [exerciseIndex, setExerciseIndex] = useState(0);
  const [doneExercises, setDoneExercises] = useState(new Set());

  // Reset carousel position when the day changes (idx-driven).
  useEffect(() => {
    setExerciseIndex(0);
    setDoneExercises(new Set());
  }, [idx]);

  /**
   * Parse the prescribed `reps` string ("8-12", "10", "30s") into a numeric
   * rep count or null for static holds.
   */
  const repsForLog = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    if (/^\d+\s*(s|sec)/i.test(s)) return null; // hold — handled separately
    const m = s.match(/(\d+)\s*[-–]\s*(\d+)/);
    if (m) return Number(m[1]);
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : null;
  };

  /**
   * "Swipe up to mark done" — auto-logs the prescribed sets × reps at zero
   * weight (or prescribed hold seconds for static holds) and advances the
   * carousel to the next exercise. Idempotent per session via doneExercises.
   */
  const quickMarkDone = async (exercise) => {
    if (!exercise || doneExercises.has(exercise.name)) {
      // Already done — just advance.
      setExerciseIndex((i) => Math.min(day.exercises.length - 1, i + 1));
      return;
    }
    setDoneExercises((s) => new Set([...s, exercise.name]));
    haptic.success();
    toast.success(`${exercise.name} marked done`);
    try {
      const isHold =
        exercise.is_static_hold === true ||
        /(\d+)\s*(s|sec)/i.test(String(exercise.reps || ""));
      if (isHold) {
        const hold = Number(exercise.hold_seconds) ||
          Number(String(exercise.reps).match(/(\d+)/)?.[1]) ||
          30;
        const total = Number(exercise.sets) || 1;
        for (let k = 0; k < total; k += 1) {
          await api.post("/workouts/log-set", {
            exercise_name: exercise.name,
            muscle_group: exercise.muscle_group,
            sets: [],
            hold_seconds: hold,
          });
        }
      } else {
        const reps = repsForLog(exercise.reps) || 8;
        const sets = Array.from({ length: Number(exercise.sets) || 3 }, () => ({
          weight_kg: 0,
          reps,
        }));
        await api.post("/workouts/log-set", {
          exercise_name: exercise.name,
          muscle_group: exercise.muscle_group,
          sets,
        });
      }
    } catch (err) {
      console.warn("Quick-mark log-set failed", err);
    }
    // Advance to next exercise after logging.
    setExerciseIndex((i) => Math.min(day.exercises.length - 1, i + 1));
  };

  // Swipe-up on a carousel slide → mark done + advance.
  const quickDoneSwipe = useSwipe({
    enabled: isMobile && !!day?.exercises?.length,
    threshold: 70,
    onSwipeUp: () => {
      const ex = day.exercises[exerciseIndex];
      if (ex) quickMarkDone(ex);
    },
  });

  // Derive which week of the multi-week plan the user is currently on. We use
  // the plan's creation date as the start; auto-advances each 7 days, capped
  // at total_weeks. This is what the /complete-session record gets tagged with.
  const currentProgramWeek = (() => {
    if (!plan) return 1;
    const total = plan.total_weeks || 1;
    const startStr = plan.created_at || plan.start_date;
    if (!startStr) return 1;
    const start = new Date(startStr);
    if (Number.isNaN(start.getTime())) return 1;
    const diffDays = Math.floor((Date.now() - start.getTime()) / (1000 * 60 * 60 * 24));
    const week = Math.floor(diffDays / 7) + 1;
    return Math.max(1, Math.min(total, week));
  })();

  const complete = async () => {
    if (!day) return;
    setCompleting(true);
    try {
      await api.post("/workouts/complete-session", {
        day: day.day,
        focus: day.focus,
        exercises_count: day.exercises?.length || 0,
        program_week: currentProgramWeek,
      });
      setCompleted(true);
      haptic.success();
      toast.success(`Week ${currentProgramWeek} · ${day.day} marked complete`);
    } catch (err) {
      console.error("complete-session failed", err);
      haptic.error();
      toast.error("Could not save session");
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#6B9BFF]" />
      </div>
    );
  }

  if (!day) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="font-display text-3xl mb-3">No workout for this day</div>
          <p className="text-white/60 mb-6">
            Generate a plan first, then pick a day to start.
          </p>
          <Link
            to="/app/workout"
            className="inline-flex bg-[#6B9BFF] text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white"
            data-testid="session-back-link"
          >
            Go to workouts
          </Link>
        </div>
      </div>
    );
  }

  const isRestDay = !day.exercises?.length;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      {/* Header */}
      <header
        className="sticky top-0 z-30 border-b border-white/10 bg-[#0a0a0a]/95 backdrop-blur"
        data-testid="session-header"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 bg-[#6B9BFF] flex items-center justify-center shrink-0">
              <Dumbbell className="w-5 h-5 text-black" />
            </div>
            <div className="min-w-0">
              <div className="label-eyebrow text-[#6B9BFF] truncate">
                {day.day} · Session
              </div>
              <div className="font-display text-xl sm:text-2xl leading-tight truncate">
                {day.focus}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {day.duration_min ? (
              <div className="hidden sm:flex items-center gap-1 text-xs text-white/60 mr-2">
                <Clock className="w-3.5 h-3.5" /> {day.duration_min} min
              </div>
            ) : null}
            <ThemeToggle />
            <button
              onClick={() => navigate("/app/workout")}
              className="inline-flex items-center gap-1 border border-white/15 hover:border-white/30 text-white/70 hover:text-white px-3 py-2 text-xs uppercase tracking-widest font-bold"
              data-testid="session-close-button"
            >
              <X className="w-3.5 h-3.5" /> Close
            </button>
          </div>
        </div>
      </header>

      {/* Body */}
      <main
        className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-3 md:py-6 pb-32"
        data-testid="session-body"
      >
        {isRestDay ? (
          <div className="border border-white/10 bg-[#121212] p-10 text-center">
            <div className="font-display text-3xl mb-2">Rest & recover</div>
            <p className="text-white/60 text-sm">
              No exercises scheduled. Hydrate, stretch, and come back stronger.
            </p>
          </div>
        ) : (
          <>
            {!isMobile && (
              <div className="flex items-center gap-2 text-white/50 text-xs mb-4">
                <Music className="w-3.5 h-3.5 text-[#6B9BFF]" />
                Music access in the bottom-right of the screen — pick a playlist and start
                moving.
              </div>
            )}

            {/* Mobile: full-screen exercise carousel. Desktop: stacked list. */}
            {isMobile ? (
              <>
                <div className="mb-3 flex items-center justify-between" data-testid="session-exercise-counter">
                  <span className="label-eyebrow text-white/40 text-[10px]">
                    Exercise {exerciseIndex + 1} of {day.exercises.length}
                    <span className="ml-2 text-[#6B9BFF]" data-testid="session-done-count">
                      ({doneExercises.size} done)
                    </span>
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-[#6B9BFF]">
                    swipe ←→ for next
                  </span>
                </div>
                <MobileCarousel
                  index={exerciseIndex}
                  onIndexChange={setExerciseIndex}
                  testid="session-exercise-carousel"
                  mobileOnly={false}
                >
                  {day.exercises.map((ex, j) => {
                    const isDone = doneExercises.has(ex.name);
                    return (
                      <div
                        key={j}
                        {...quickDoneSwipe.bind}
                        className={isDone ? "opacity-60" : ""}
                      >
                        {isDone && (
                          <div
                            className="mb-2 border border-[#34C759]/40 bg-[#34C759]/10 px-3 py-2 text-xs text-[#34C759] inline-flex items-center gap-2"
                            data-testid={`session-exercise-done-badge-${j}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Marked done — swipe to next
                          </div>
                        )}
                        <ExerciseCard
                          exercise={ex}
                          dayIndex={idx}
                          exIndex={j}
                          defaultExpanded
                          compact
                        />
                        {!isDone && (
                          <button
                            type="button"
                            onClick={() => quickMarkDone(ex)}
                            className="mt-3 w-full bg-[#1A1A1A] border border-dashed border-white/15 hover:border-[#34C759] text-white/60 hover:text-[#34C759] py-3 text-xs uppercase tracking-widest font-bold inline-flex items-center justify-center gap-2 transition-colors"
                            data-testid={`session-quick-done-${j}`}
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                            Swipe up or tap to mark done
                          </button>
                        )}
                      </div>
                    );
                  })}
                </MobileCarousel>
              </>
            ) : (
              <div className="space-y-3" data-testid="session-exercise-list">
                {day.exercises.map((ex, j) => (
                  <ExerciseCard
                    key={j}
                    exercise={ex}
                    dayIndex={idx}
                    exIndex={j}
                    defaultExpanded
                  />
                ))}
              </div>
            )}

            <button
              onClick={complete}
              disabled={completing || completed}
              className={`mt-8 w-full font-black uppercase tracking-widest px-8 py-5 inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60 ${
                completed
                  ? "bg-[#34C759] text-black"
                  : "bg-[#6B9BFF] text-black hover:bg-white"
              }`}
              data-testid="session-complete-button"
            >
              {completing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                </>
              ) : completed ? (
                <>
                  <CheckCircle2 className="w-4 h-4" /> Session complete — nice work
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" /> Mark session complete
                </>
              )}
            </button>

            {!user?.spotify_playlist_url && (
              <p className="text-xs text-white/40 mt-4 text-center">
                Tip: add a Spotify playlist URL via the music button to soundtrack your
                session.
              </p>
            )}
          </>
        )}
      </main>
    </div>
  );
}
